import { ArrowLeft, MoreVertical, Plus, X, Calendar } from "lucide-react";
import { SearchNormal } from "iconsax-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import instance from "../../../../../../axios/instance";

const ChangeTreatmentModal = ({
  isOpen,
  onClose,
  onSave,
  currentTreatment,
}) => {
  const [products, setProducts] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [formData, setFormData] = useState({
    productId: "",
    treatmentId: "",
    medication: "",
  });
  const [loading, setLoading] = useState(false);
  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      const response = await instance.get("/doctor/cms/products", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    }
  };

  const fetchTreatments = async (productId) => {
    try {
      const response = await instance.get(
        `/doctor/cms/treatments?productId=${productId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTreatments(Array.isArray(response.data) ? response.data : (response.data?.data || []));
    } catch (error) {
      console.error("Error fetching treatments:", error);
      toast.error("Failed to load treatments");
    }
  };

  const handleProductChange = (productId) => {
    setFormData({ ...formData, productId, treatmentId: "", medication: "" });
    if (productId) {
      fetchTreatments(productId);
    } else {
      setTreatments([]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Error changing treatment:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Change Treatment</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Choose Product
            </label>
            <select
              value={formData.productId}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Choose Treatment
            </label>
            <select
              value={formData.treatmentId}
              onChange={(e) =>
                setFormData({ ...formData, treatmentId: e.target.value })
              }
              disabled={!formData.productId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">Select a treatment</option>
              {treatments.map((treatment) => (
                <option key={treatment.id} value={treatment.id}>
                  {treatment.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medication (ml)
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.medication}
              onChange={(e) =>
                setFormData({ ...formData, medication: e.target.value })
              }
              placeholder="Enter dosage in ml"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              loading ||
              !formData.productId ||
              !formData.treatmentId ||
              !formData.medication
            }
            className="px-4 py-2 bg-primary text-white rounded-full hover:bg-primary/50 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChangeSubscriptionModal = ({
  isOpen,
  onClose,
  onSave,
  currentSubscription,
}) => {
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDays("30");
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSave(days);
      onClose();
      setDays("30");
    } catch (error) {
      console.error("Error changing subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const subscriptionPlans = [
    { value: "30", label: "30 Days" },
    { value: "60", label: "60 Days" },
    { value: "90", label: "90 Days" },
    { value: "180", label: "180 Days" },
    { value: "365", label: "365 Days (1 Year)" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Change Subscription Plan</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Subscription Duration
            </label>
            <select
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose duration</option>
              {subscriptionPlans.map((plan) => (
                <option key={plan.value} value={plan.value}>
                  {plan.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Or Enter Custom Days
            </label>
            <input
              type="number"
              min="1"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="Enter number of days"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => {
              onClose();
              setDays("");
            }}
            className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !days || days <= 0}
            className="px-4 py-2 bg-primary text-white rounded-full hover:bg-primary/50 disabled:opacity-50"
          >
            {loading ? "Changing..." : "Change Plan"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddRecordModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    checkInDate: "",
    dosage: "",
    patientWeight: "",
    notes: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setFormData({ checkInDate: "", dosage: "", patientWeight: "", notes: "" });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-[507px]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Add Medication History Record
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Check-in Date
              </label>
              <input
                type="date"
                value={formData.checkInDate}
                onChange={(e) =>
                  setFormData({ ...formData, checkInDate: e.target.value })
                }
                required
                className="mt-1 block w-full h-9 px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Dosage
              </label>
              <input
                type="text"
                value={formData.dosage}
                onChange={(e) =>
                  setFormData({ ...formData, dosage: e.target.value })
                }
                placeholder="e.g., 0.5mg"
                required
                className="mt-1 block w-full h-9 px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Patient Weight
              </label>
              <input
                type="text"
                value={formData.patientWeight}
                onChange={(e) =>
                  setFormData({ ...formData, patientWeight: e.target.value })
                }
                placeholder="e.g., 180 lbs"
                className="mt-1 block w-full h-9 px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Notes (optional)
              </label>
              <textarea
                rows="3"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Add any relevant notes..."
                className="mt-1 block w-full h-[120px] px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              ></textarea>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-full hover:bg-opacity-90"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditRecordModal = ({ isOpen, onClose, onSave, record }) => {
  const [formData, setFormData] = useState({
    checkInDate: "",
    dosage: "",
    patientWeight: "",
    notes: "",
  });

  useEffect(() => {
    if (record) {
      setFormData({
        checkInDate: record.check_in_date
          ? record.check_in_date.split("T")[0]
          : "",
        dosage: record.dosage || "",
        patientWeight: record.patient_weight || "",
        notes: record.notes || "",
      });
    }
  }, [record]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-[507px]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Edit Medication History Record
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Check-in Date
              </label>
              <input
                type="date"
                value={formData.checkInDate}
                onChange={(e) =>
                  setFormData({ ...formData, checkInDate: e.target.value })
                }
                required
                className="mt-1 block w-full h-9 px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Dosage
              </label>
              <input
                type="text"
                value={formData.dosage}
                onChange={(e) =>
                  setFormData({ ...formData, dosage: e.target.value })
                }
                placeholder="e.g., 0.5ml"
                required
                className="mt-1 block w-full h-9 px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Patient Weight
              </label>
              <input
                type="text"
                value={formData.patientWeight}
                onChange={(e) =>
                  setFormData({ ...formData, patientWeight: e.target.value })
                }
                placeholder="e.g., 180 lbs"
                className="mt-1 block w-full h-9 px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                rows="3"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Add any relevant notes..."
                className="mt-1 block w-full h-[120px] px-2 border border-[#27272A26] rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              ></textarea>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-full hover:bg-opacity-90"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, record }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete this record? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(record)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const CheckInHistory = ({
  records,
  onAddRecord,
  onEditRecord,
  onDeleteRecord,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  if (records.length === 0) {
    return (
      <div className="text-center py-12 rounded-lg">
        <p className="text-gray-500 mb-4">
          No records found for this treatment.
        </p>
        <button
          onClick={onAddRecord}
          className="bg-primary text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-opacity-90 flex items-center gap-2 mx-auto"
        >
          <Plus size={16} />
          Add Record
        </button>
      </div>
    );
  }

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
            <th className="p-3">Check-in Date</th>
            <th className="p-3">Dosage</th>
            <th className="p-3">Doctor</th>
            <th className="p-3">Notes</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {records.map((record) => (
            <tr key={record.id}>
              <td className="p-3 text-sm text-gray-800">
                {new Date(record.check_in_date).toLocaleDateString()}
              </td>
              <td className="p-3 text-sm text-gray-800">
                {record.dosage}mg/weekly
              </td>
              <td className="p-3 text-sm text-gray-800">
                {record.doctor_name}
              </td>
              <td className="p-3 text-sm text-gray-600">
                {record.notes || "N/A"}
              </td>
              <td className="p-3 text-sm text-right relative" ref={openDropdown === record.id ? dropdownRef : null}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(openDropdown === record.id ? null : record.id);
                  }}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <MoreVertical size={18} />
                </button>
                {openDropdown === record.id && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg z-10 border">
                    <button
                      onClick={() => {
                        onEditRecord(record);
                        setOpenDropdown(null);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        onDeleteRecord(record);
                        setOpenDropdown(null);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DateFilterModal = ({ isOpen, onClose, dateRange, setDateRange }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Filter by Date</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => {
              setDateRange({ startDate: "", endDate: "" });
              onClose();
            }}
            className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50"
          >
            Clear
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-full hover:bg-primary/50"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export const Treatments = ({ selectedRow, triggerEditModal, onViewChange, onRefresh }) => {
  const [treatments, setTreatments] = useState([]);
  const [filteredTreatments, setFilteredTreatments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [view, setView] = useState("list");
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const [isChangeTreatmentOpen, setIsChangeTreatmentOpen] = useState(false);
  const [isChangeSubscriptionOpen, setIsChangeSubscriptionOpen] =
    useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [medicationRecords, setMedicationRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = window.sessionStorage.getItem("ACCESS_TOKEN");
  const mountedTrigger = useRef(triggerEditModal);

  useEffect(() => {
    if (onViewChange) {
      onViewChange(view);
    }
  }, [view]);

  useEffect(() => {
    if (triggerEditModal > 0 && triggerEditModal !== mountedTrigger.current) {
      if (view === "list") {
        setIsChangeTreatmentOpen(true);
      } else if (view === "details") {
        setIsAddModalOpen(true);
      }
    }
  }, [triggerEditModal, view]);

  useEffect(() => {
    if (selectedRow?.treatmentsList) {
      setTreatments(selectedRow.treatmentsList);
      setFilteredTreatments(selectedRow.treatmentsList);
    }
  }, [selectedRow]);

  useEffect(() => {
    let filtered = treatments;

    if (searchQuery) {
      filtered = filtered.filter((treatment) =>
        treatment.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (selectedStatus !== "All Status") {
      filtered = filtered.filter((t) => t.status === selectedStatus);
    }

    if (dateRange.startDate && dateRange.endDate) {
      filtered = filtered.filter((t) => {
        const treatmentDate = new Date(t.updated);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        return treatmentDate >= start && treatmentDate <= end;
      });
    }

    setFilteredTreatments(filtered);
  }, [searchQuery, selectedStatus, dateRange, treatments]);

  const fetchMedicationRecords = async (subscriptionId) => {
    setLoading(true);
    try {
      const response = await instance.get(
        `/doctor/medication-records/${subscriptionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setMedicationRecords(response.data.records || []);
    } catch (error) {
      console.error("Error fetching medication records:", error);
      toast.error("Failed to load medication records");
      setMedicationRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (treatment) => {
    setSelectedTreatment(treatment);
    setView("details");
    if (treatment.subscriptionId) {
      fetchMedicationRecords(treatment.subscriptionId);
    }
  };

  const handleGoBack = () => {
    setSelectedTreatment(null);
    setView("list");
    setMedicationRecords([]);
  };

  const handleChangeTreatment = async (formData) => {
    setLoading(true);
    try {
      await instance.post(
        `/doctor/subscription/${selectedRow.id}/change-treatment`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Treatment changed successfully!");
      if (onRefresh) onRefresh();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to change treatment";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeSubscription = async (days) => {
    setLoading(true);
    try {
      await instance.put(
        `/doctor/subscription/${selectedRow.id}/change-plan`,
        { days: parseInt(days) },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Subscription plan changed successfully!");
    } catch (error) {
      console.error("Error changing subscription:", error);
      toast.error("Failed to change subscription plan");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecord = async (formData) => {
    setLoading(true);
    try {
      await instance.post(
        `/doctor/medication-records`,
        {
          subscriptionId: selectedTreatment.subscriptionId,
          checkInDate: formData.checkInDate,
          dosage: formData.dosage,
          notes: formData.notes,
          patientWeight: formData.patientWeight,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Medication record added successfully!");
      fetchMedicationRecords(selectedTreatment.subscriptionId);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Error saving record:", error);
      toast.error("Failed to save medication record");
    } finally {
      setLoading(false);
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (formData) => {
    setLoading(true);
    try {
      await instance.put(
        `/doctor/medication-records/${selectedRecord.id}`,
        {
          checkInDate: formData.checkInDate,
          dosage: formData.dosage,
          notes: formData.notes,
          patientWeight: formData.patientWeight,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Medication record updated successfully!");
      fetchMedicationRecords(selectedTreatment.subscriptionId);
      setIsEditModalOpen(false);
      setSelectedRecord(null);
    } catch (error) {
      console.error("Error updating record:", error);
      toast.error("Failed to update medication record");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await instance.delete(`/doctor/medication-records/${selectedRecord.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Medication record deleted successfully!");
      fetchMedicationRecords(selectedTreatment.subscriptionId);
      setIsDeleteModalOpen(false);
      setSelectedRecord(null);
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete medication record");
    } finally {
      setLoading(false);
    }
  };

  if (view === "details") {
    return (
      <div className="mt-6">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-sm font-medium text-primary mb-4"
        >
          <ArrowLeft size={16} />
          Back to Treatments
        </button>
        <div className="p-4">
          <div className="mb-6">
            <h3 className="font-medium text-2xl">{selectedTreatment.name}</h3>
            <p className="text-base text-gray-500 mt-1">
              Delivery Type:{" "}
              <span className="font-medium text-gray-800">Injection</span>
            </p>
          </div>
          <CheckInHistory
            records={medicationRecords}
            onAddRecord={() => setIsAddModalOpen(true)}
            onEditRecord={handleEditRecord}
            onDeleteRecord={handleDeleteRecord}
          />
        </div>
        <AddRecordModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleSaveRecord}
        />
        <EditRecordModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveEdit}
          record={selectedRecord}
        />
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          record={selectedRecord}
        />
      </div>
    );
  }

  const statuses = ["All Status", "active", "inactive", "pending", "cancelled"];

  return (
    <div className="mt-6">
      <div className="mb-[22px]">
        <div className="flex flex-col lg:flex-row justify-between gap-3">
          <div className="max-w-[400px] w-full relative h-full">
            <SearchNormal
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search treatments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-3">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-dashed border-gray-300 rounded-full text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white cursor-pointer"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <button
              onClick={() => setIsDateFilterOpen(true)}
              className="px-4 py-2 border border-dashed border-gray-300 rounded-full text-[14px] hover:bg-gray-50 flex items-center gap-2 font-medium"
            >
              <Calendar size={18} />
              Date
            </button>
          </div>
        </div>
      </div>

      <table className="w-full">
        <thead>
          <tr className="text-left text-sm text-gray-500 uppercase tracking-wider border-b">
            <th className="p-3">Name</th>
            <th className="p-3">Price</th>
            <th className="p-3">Status</th>
            <th className="p-3">Updated</th>
            <th className="p-3 text-right"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredTreatments.length === 0 ? (
            <tr>
              <td colSpan="5" className="p-8 text-center text-gray-500">
                No treatments found
              </td>
            </tr>
          ) : (
            filteredTreatments.map((treatment, index) => (
              <tr
                key={treatment.subscriptionId || index}
                onClick={() => handleViewDetails(treatment)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="p-3 text-sm text-gray-900">{treatment.name}</td>
                <td className="p-3 text-sm text-gray-900">
                  ${treatment.price ? treatment.price.toFixed(2) : "0.00"}/mo
                </td>
                <td className="p-3 text-sm">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${treatment.status === "active"
                      ? "bg-green-100 text-green-800"
                      : treatment.status === "cancelled"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                      }`}
                  >
                    {treatment.status || "active"}
                  </span>
                </td>
                <td className="p-3 text-sm text-gray-600">
                  {treatment.updated
                    ? new Date(treatment.updated).toLocaleDateString()
                    : "N/A"}
                </td>
                <td className="p-3 text-sm text-right relative" ref={openDropdown === index ? dropdownRef : null}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(openDropdown === index ? null : index);
                    }}
                    className="p-1 rounded-full hover:bg-gray-100"
                  >
                    <MoreVertical size={18} />
                  </button>
                  {openDropdown === index && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsChangeSubscriptionOpen(true);
                          setOpenDropdown(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Change Subscription
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <ChangeTreatmentModal
        isOpen={isChangeTreatmentOpen}
        onClose={() => setIsChangeTreatmentOpen(false)}
        onSave={handleChangeTreatment}
        currentTreatment={selectedTreatment}
      />

      <ChangeSubscriptionModal
        isOpen={isChangeSubscriptionOpen}
        onClose={() => setIsChangeSubscriptionOpen(false)}
        onSave={handleChangeSubscription}
        currentSubscription={selectedRow}
      />

      <DateFilterModal
        isOpen={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
    </div>
  );
};
